import type { PublicSchedulingRules } from "@/lib/api/public-consumer"

// Which days and times the consumer can book, from the shop's scheduling
// rules. Pure: "now" is passed in, so today's cut-off is testable.

export type AppointmentType = "inspection" | "drop_off"

// Today, nothing that has started or starts within the hour.
const LEAD_MINUTES = 60

export function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

// Deterministic slots, every slot available (no random flicker). Inspections
// use the configured slot duration; drop-offs are hourly.
export function buildTimeSlots(
  rules: PublicSchedulingRules,
  type: AppointmentType,
  day: Date,
  now: Date,
): string[] {
  const startHour = type === "inspection" ? rules.inspection_start_hour : rules.dropoff_start_hour
  const endHour = type === "inspection" ? rules.inspection_end_hour : rules.dropoff_end_hour
  const duration = type === "inspection" ? rules.inspection_slot_duration : 60
  const isToday = day.toDateString() === now.toDateString()
  const cutoff = now.getHours() * 60 + now.getMinutes() + LEAD_MINUTES
  const slots: string[] = []
  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += duration) {
      if (h + m / 60 >= endHour) break
      if (isToday && h * 60 + m < cutoff) continue
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`)
    }
  }
  return slots
}

// Days in `month` the shop takes this kind of appointment, from today on
// (drop-offs after the shop's buffer), leaving out a today with no time left.
export function buildAvailableDates(
  rules: PublicSchedulingRules,
  type: AppointmentType,
  month: Date,
  now: Date,
): Date[] {
  const days = type === "inspection" ? rules.inspection_days : rules.dropoff_days
  const min = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (type === "drop_off") min.setDate(min.getDate() + rules.dropoff_buffer_days)

  const out: Date[] = []
  const end = new Date(month.getFullYear(), month.getMonth() + 1, 0)
  for (let d = new Date(month.getFullYear(), month.getMonth(), 1); d <= end; d.setDate(d.getDate() + 1)) {
    if (!days.includes(d.getDay()) || d < min) continue
    if (buildTimeSlots(rules, type, d, now).length === 0) continue
    out.push(new Date(d))
  }
  return out
}
